import {
  CardData,
  CardType,
  UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT,
} from "../../constants/GlobalTypesAndEnumsAndConstants";

type RatingHistoryEntry = {
  understanding_rating?: unknown;
  spaced_repetition_id_of_knowledge_rating?: unknown;
};

type CardRatingsForUser = {
  most_recent_knowledge_understanding_rating?: unknown;
  most_recent_spaced_repetition_knowledge_rating?: unknown;
  most_recent_spaced_repetition_id_of_knowledge_rating?: unknown;
  understanding_ratings_history_object_list?: RatingHistoryEntry[];
};

// These MCQ formats share the same correctness threshold.
const isMultipleChoiceCard = (card: CardData): boolean =>
  card.card_type === CardType.RegularMultipleChoiceQuestion ||
  card.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
  card.card_type === CardType.USMLEStep1MCQ;

const isMistakeRating = (card: CardData, rating: unknown): boolean => {
  if (typeof rating !== "number") return false;
  return isMultipleChoiceCard(card) ? rating < UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT : rating === 1;
};

export function cardHasPastMistake(
  card: CardData,
  userId: string | undefined,
  options?: {
    isDailyReview?: boolean;
    currentReviewId?: string;
    includeHistoricalMistakes?: boolean;
  }
): boolean {
  if (!card || !userId) return false;

  const ratings = card.understanding_ratings_for_card_by_user?.[userId] as CardRatingsForUser | undefined;
  if (!ratings) return false;

  const history = Array.isArray(ratings.understanding_ratings_history_object_list)
    ? ratings.understanding_ratings_history_object_list.filter(Boolean)
    : [];

  if (options?.isDailyReview) {
    // Daily review retries must reflect the latest rating in this session. The
    // history is append-only, so an earlier wrong retry is superseded by a
    // later correct retry for the same review.
    return (
      ratings.most_recent_spaced_repetition_id_of_knowledge_rating === options.currentReviewId &&
      isMistakeRating(card, ratings.most_recent_spaced_repetition_knowledge_rating)
    );
  }

  if (options?.includeHistoricalMistakes && history.length > 0) {
    // A later correct answer must not erase an earlier incorrect answer from
    // the deck's historical mistake review set.
    return history.some((entry) => isMistakeRating(card, entry.understanding_rating));
  }

  return isMistakeRating(card, ratings.most_recent_knowledge_understanding_rating);
}
